import { useCallback, useContext } from "react";

// internal-deps
import { AnalyticsContext } from "@swiggy-private/checkout-core";
import {
    clickApplyCouponBtnEvent,
    clickCouponOffersEvent,
    clickMoreDetailsEvent,
    clickOfferCroutonEvent,
    clickRemoveCoupon,
    impressionCouponOffersEvent,
    impressionOffersEvent,
    impressionPaymentBannerEvent,
    impressionRemoveCoupon,
    impressionRewardOffersEvent,
    impressionOfferCroutonEvent,
    impressionHalfCardInvalidCoupon,
    clickHalfCardInvalidCoupon,
} from "@cart-client/analytics/events/CouponListing";

// interfaces
import { CouponCardImpressionPayload } from "@swiggy-private/core-types";
import { GroupOrderState } from "@swiggy-private/cart-types";
import { ReviewedCart } from "@cart-client/interfaces/Cart";
import { ClickCouponOffersParams, CouponAnalytics } from "../types";

// constants
import { CART_GENERIC_EVENT_CASES } from "@swiggy-private/cart-ui";

// helpers
import { recordGenericErrorNREvent } from "@swiggy-private/checkout-core";

export const useCouponAnalytics = (groupCartState: GroupOrderState, cartId: string): CouponAnalytics => {
    const { clickEvent, impressionEvent } = useContext(AnalyticsContext);

    const handleCouponOffersImpression = useCallback(
        (payload: CouponCardImpressionPayload) => {
            impressionCouponOffersEvent(impressionEvent, {
                ...payload,
                offerId: payload.offerId || "",
                isApplicable: payload.isApplicable || false,
                couponCode: payload.couponCode || "",
                groupCartId: groupCartState?.data?.group_cart_id,
            });
        },
        [groupCartState?.data?.group_cart_id, impressionEvent],
    );

    const handleRewardOfferImpression = useCallback(
        ({ offerId = "", isApplicable = false, couponName, couponType }: CouponCardImpressionPayload) => {
            impressionRewardOffersEvent(
                impressionEvent,
                {
                    offerId,
                    isApplicable,
                    couponName,
                    couponType,
                },
                groupCartState?.data?.group_cart_id,
            );
        },
        [groupCartState?.data?.group_cart_id, impressionEvent],
    );

    const handlePaymentBannerImpression = useCallback(
        (bannerText: string) => {
            impressionPaymentBannerEvent(impressionEvent, bannerText, groupCartState?.data?.group_cart_id);
        },
        [groupCartState?.data?.group_cart_id, impressionEvent],
    );

    const handleImpressionErrorPage = useCallback(
        () =>
            recordGenericErrorNREvent({
                errorContext: CART_GENERIC_EVENT_CASES.EMPTY_COUPON_LISTING_PAGE,
            }),
        [],
    );

    const handleImpressionRemoveCoupon = useCallback(() => {
        impressionRemoveCoupon(impressionEvent, cartId);
    }, [cartId, impressionEvent]);

    const handleImpressionOfferCrouton = useCallback(
        (croutonTitle: string, cartResponse: ReviewedCart | null) => {
            impressionOfferCroutonEvent(
                impressionEvent,
                croutonTitle,
                groupCartState?.data?.group_cart_id,
                cartResponse?.data?.cartId,
            );
        },
        [impressionEvent, groupCartState?.data?.group_cart_id],
    );

    const handleClickMoreDetails = useCallback(
        (couponCode: string, couponType: string) => {
            clickMoreDetailsEvent(clickEvent, couponCode, couponType);
        },
        [clickEvent],
    );

    const handleClickOfferCrouton = useCallback(
        (croutonTitle: string, cartResponse: ReviewedCart | null) => {
            clickOfferCroutonEvent(
                clickEvent,
                croutonTitle,
                groupCartState?.data?.group_cart_id,
                cartResponse?.data?.cartId,
            );
        },
        [clickEvent, groupCartState?.data?.group_cart_id],
    );

    const handleClickApplyCoupon = useCallback(
        (couponCode: string) => {
            clickApplyCouponBtnEvent(clickEvent, couponCode, groupCartState?.data?.group_cart_id);
        },
        [clickEvent, groupCartState?.data?.group_cart_id],
    );

    const handleClickCouponOffers = useCallback(
        ({
            couponCode = "",
            couponType = "",
            offerId = "",
            isApplicable = false,
            timerPayload,
            storeId = "",
        }: ClickCouponOffersParams) => {
            clickCouponOffersEvent(
                clickEvent,
                { couponCode, couponType },
                {
                    storeId,
                    offerId,
                    isApplicable: String(isApplicable),
                    timerPayload,
                },
            );
        },
        [clickEvent],
    );

    const handleClickRemoveCoupon = useCallback(
        (couponCode: string, couponType: string) => {
            clickRemoveCoupon(clickEvent, cartId, couponCode, couponType);
        },
        [clickEvent, cartId],
    );

    const handleClickHalfCardInvalidCoupon = useCallback(
        (errorMessage: string) => {
            clickHalfCardInvalidCoupon(clickEvent, errorMessage, cartId);
        },
        [clickEvent, cartId],
    );

    const handleImpressionHalfCardInvalidCoupon = useCallback(
        (text: string) => {
            impressionHalfCardInvalidCoupon(impressionEvent, text, cartId);
        },
        [impressionEvent, cartId],
    );

    const handleImpressionOffers = useCallback(
        (groupCartId: string) => {
            impressionOffersEvent(impressionEvent, groupCartId);
        },
        [impressionEvent],
    );

    return {
        handleCouponOffersImpression,
        handleRewardOfferImpression,
        handlePaymentBannerImpression,
        handleImpressionErrorPage,
        handleImpressionRemoveCoupon,
        handleImpressionOfferCrouton,
        handleClickMoreDetails,
        handleClickOfferCrouton,
        handleClickApplyCoupon,
        handleClickCouponOffers,
        handleClickRemoveCoupon,
        handleClickHalfCardInvalidCoupon,
        handleImpressionHalfCardInvalidCoupon,
        handleImpressionOffers,
    };
};
